import type { DataSource, Measure, Relationship } from '@numbernode/contracts';

export type ModelIssue = { severity: 'warning' | 'error'; message: string; relationshipId?: string };

export function validateModel(sources: DataSource[], relationships: Relationship[]): ModelIssue[] {
  const tables = new Map(sources.map((source) => [source.tableName, source]));
  const issues: ModelIssue[] = [];
  for (const relationship of relationships) {
    const from = tables.get(relationship.fromTable);
    const to = tables.get(relationship.toTable);
    if (!from || !to) {
      issues.push({ severity: 'error', message: 'Relationship references a missing table.', relationshipId: relationship.id });
      continue;
    }
    if (!from.columns.some((column) => column.name === relationship.fromColumn)) {
      issues.push({ severity: 'error', message: `${relationship.fromTable}.${relationship.fromColumn} does not exist.`, relationshipId: relationship.id });
    }
    if (!to.columns.some((column) => column.name === relationship.toColumn)) {
      issues.push({ severity: 'error', message: `${relationship.toTable}.${relationship.toColumn} does not exist.`, relationshipId: relationship.id });
    }
  }
  const seen = new Set<string>();
  for (const relationship of relationships.filter((item) => item.active)) {
    const key = [relationship.fromTable, relationship.toTable].sort().join('::');
    if (seen.has(key)) issues.push({ severity: 'warning', message: `Multiple active relationship paths exist between ${relationship.fromTable} and ${relationship.toTable}.`, relationshipId: relationship.id });
    seen.add(key);
  }
  return issues;
}

export function suggestMeasures(source: DataSource): Measure[] {
  return source.columns
    .filter((column) => column.semanticType === 'measure')
    .slice(0, 8)
    .map((column) => ({
      id: `measure_${source.id}_${column.name}`,
      name: `Total ${column.displayName ?? column.name}`,
      tableName: source.tableName,
      expression: `SUM(\"${column.name.replaceAll('"', '""')}\")`,
      format: '#,##0.00',
      description: `Suggested aggregate for ${column.name}`,
    }));
}
